// Single source of truth for the article catalogue, used by the /blog index
// and the related-article cards. To add an article: add an entry here, create
// its component, and register the slug in gids/Guide.tsx + scripts/routes.mjs.
//
// There is ONE content section, served at /blog (see CONTENT.md). The legacy
// /gids paths 301-redirect to /blog (public/_redirects) to keep SEO authority
// on a single URL space.

export interface GuideMeta {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readingTime: number;
}

/** Canonical path for an article. Everything lives under /blog. */
export function articlePath(article: GuideMeta): string {
  return `/blog/${article.slug}`;
}

export const GUIDES: GuideMeta[] = [
  {
    slug: "real-estate-social-media-marketing",
    title: "Real estate social media marketing: a system for agents",
    excerpt:
      "A repeatable social media system for agents: four content pillars, a sustainable cadence, lead capture and a worked cost-per-lead example.",
    category: "Real estate agents",
    readingTime: 13,
  },
  {
    slug: "buyer-consultation-guide",
    title: "How to conduct a real estate buyer consultation",
    excerpt:
      "A buyer consultation agenda that qualifies clients, aligns on budget and criteria, and sets expectations before any home tours. With a worked example.",
    category: "Real estate agents",
    readingTime: 12,
  },
  {
    slug: "real-estate-referrals-guide",
    title: "How to ask for and win real estate referrals",
    excerpt:
      "Turn word of mouth into a system: when to ask for real estate referrals, exactly what to say, and how to stay top of mind between transactions.",
    category: "Real estate agents",
    readingTime: 12,
  },
  {
    slug: "speed-to-lead-real-estate",
    title: "Speed to lead: why response time wins listings",
    excerpt:
      "Why speed to lead quietly decides which agent wins the listing, how fast to respond, and how to build a system that answers every enquiry in minutes.",
    category: "Real estate agents",
    readingTime: 12,
  },
  {
    slug: "mortgage-term-explained",
    title: "Mortgage term: how loan length changes cost",
    excerpt:
      "What the mortgage term really controls: monthly payment, total interest, equity build and affordability — with a 20/25/30/35-year worked comparison.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "gifted-deposit-mortgage-explained",
    title: "Gifted deposit: how it affects a mortgage application",
    excerpt:
      "How lenders assess a gifted deposit: donor eligibility, the gift letter, source-of-funds checks, gift versus loan, and what a gift is worth in rate terms.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "buy-to-let-mortgage-affordability",
    title: "Buy-to-let mortgage affordability and rental coverage",
    excerpt:
      "How buy-to-let lending is really sized: the rental coverage ratio, stress rates, tax bands, top-slicing, and a worked example of the maximum loan.",
    category: "Mortgage",
    readingTime: 12,
  },
  {
    slug: "how-lenders-treat-variable-income",
    title: "How lenders treat bonus, commission and overtime income",
    excerpt:
      "How lenders assess bonus, commission and overtime on a mortgage application: averaging, lower-of rules, haircuts, the documents required, and a worked example.",
    category: "Mortgage",
    readingTime: 12,
  },
  {
    slug: "mortgage-in-principle-explained",
    title: "Mortgage in principle explained: what an AIP really proves",
    excerpt:
      "What an agreement in principle is issued against, soft vs hard credit searches, why it expires, why applications still fail — and the four checks agents should run on one.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "pre-approval-vs-pre-qualification",
    title: "Mortgage pre-approval vs pre-qualification, explained",
    excerpt:
      "What each one proves, why sellers trust a pre-approval over a pre-qualification, and how advisors and agents use the difference — with a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "cash-offer-vs-financed-offer",
    title: "Cash offer vs financed offer: does cash really win?",
    excerpt:
      "What actually makes a cash offer win, when a strong financed offer beats it, how to verify cash is real, and how agents compare the two — with a worked example.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "counter-offer-strategy-buying-a-house",
    title: "How to handle a counteroffer when buying a house",
    excerpt:
      "How to read a seller's counteroffer, respond with price and terms, and know when to counter, accept or walk — anchored to value, not their number.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "how-to-write-an-offer-letter",
    title: "How to write a winning offer letter to a seller",
    excerpt:
      "What to include, the tone that wins, the fair-housing lines to avoid, and how agents pair a buyer's letter with a defensible price to break the tie.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "best-and-final-offer-strategy",
    title: "Best and final offer strategy: how to win without overpaying",
    excerpt:
      "What a best and final offer means, how to price your one shot from comps, use terms as well as price, and win the home without overpaying.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "real-estate-contingencies-explained",
    title: "Contingencies in a real estate offer explained",
    excerpt:
      "What contingencies in a real estate offer are — inspection, appraisal, financing and sale — how they protect buyers, and the real risk of waiving them.",
    category: "Buying",
    readingTime: 12,
  },
  {
    slug: "earnest-money-deposit-explained",
    title: "Earnest money deposit: how much and how it works",
    excerpt:
      "What an earnest money deposit is, how much to put down, where it's held, and when a buyer gets it back — with a worked example for agents and buyers.",
    category: "Buying",
    readingTime: 11,
  },
  {
    slug: "escalation-clause-explained",
    title: "What is an escalation clause in real estate?",
    excerpt:
      "What an escalation clause is, how it works, when it helps and when it backfires — with a worked example and the cap every buyer's agent should set.",
    category: "Negotiation",
    readingTime: 12,
  },
  {
    slug: "real-estate-farming-strategy",
    title: "Real estate geographic farming strategy for agents",
    excerpt:
      "How to build a real estate farming strategy: choose and size a farm, plan consistent contact, and measure ROI. A practical guide for agents, with an example.",
    category: "Real estate agents",
    readingTime: 12,
  },
  {
    slug: "cma-vs-appraisal-difference",
    title: "CMA vs appraisal: what is the difference?",
    excerpt:
      "What a CMA and an appraisal each are, who does them, what they cost, how they differ and when you need which — with a worked example for agents and clients.",
    category: "Valuation",
    readingTime: 11,
  },
  {
    slug: "pricing-pyramid-real-estate",
    title: "The real estate pricing pyramid explained",
    excerpt:
      "The pricing pyramid shows how overpricing shrinks your buyer pool. How it works, why buyers vanish as price climbs, and how to use it with sellers. With a worked example.",
    category: "Pricing",
    readingTime: 11,
  },
  {
    slug: "how-to-handle-multiple-offers",
    title: "How to handle multiple offers as a listing agent",
    excerpt:
      "How to handle multiple offers as a listing agent: the three response strategies, scoring offers beyond price, and a worked example to guide the seller.",
    category: "Negotiation",
    readingTime: 12,
  },
  {
    slug: "sellers-net-sheet-explained",
    title: "Seller's net sheet: how to estimate seller proceeds",
    excerpt:
      "A seller's net sheet shows what a seller really keeps after payoff, commission and closing costs. How agents build one to set expectations, with a worked example.",
    category: "Selling",
    readingTime: 12,
  },
  {
    slug: "when-to-reduce-asking-price",
    title: "When to reduce the asking price on a listing",
    excerpt:
      "When to cut the asking price on a listing: the signals that justify a reduction, how to time it, how big a cut to make, and how to coach the seller. With a worked example.",
    category: "Pricing",
    readingTime: 12,
  },
  {
    slug: "valuation-confidence-score-explained",
    title: "What is a valuation confidence score (and why it matters)",
    excerpt:
      "A valuation confidence score shows how reliable an estimate is. What it measures, what drives it up or down, how to read the confidence band, and how agents use it. With a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "property-valuation-data-sources",
    title: "What data sources do property valuations and AVMs use?",
    excerpt:
      "What data property valuations and AVMs use — sold comps, listings, public records, geospatial and condition data — and why the data layer decides accuracy. With a worked example.",
    category: "Valuation",
    readingTime: 13,
  },
  {
    slug: "sale-to-list-price-ratio",
    title: "Sale-to-list price ratio explained: how to read it",
    excerpt:
      "Sale-to-list price ratio explained: how to calculate it, what it signals about market balance, and how agents use it to price listings and coach buyers. With a worked example.",
    category: "Market analysis",
    readingTime: 12,
  },
  {
    slug: "days-on-market-explained",
    title: "Days on market (DOM): what it tells you about pricing",
    excerpt:
      "What days on market really signals about pricing: how to calculate DOM, read CDOM, spot a stale listing and use median DOM with clients. With a worked example.",
    category: "Market analysis",
    readingTime: 12,
  },
  {
    slug: "how-to-handle-a-low-appraisal",
    title: "How to handle a low appraisal (appraisal gap)",
    excerpt:
      "A low appraisal threatens the deal. How agents, buyers and advisors close an appraisal gap — renegotiate, challenge, cover it or restructure — with a worked example.",
    category: "Valuation",
    readingTime: 13,
  },
  {
    slug: "subject-to-sale-offer-explained",
    title: "Subject to sale: making an offer before yours sells",
    excerpt:
      "What a subject-to-sale offer is, why sellers hesitate, and how buyers' agents make one competitive in a chain — with a worked example.",
    category: "Buying",
    readingTime: 12,
  },
  {
    slug: "appraisal-gap-clause-explained",
    title: "Appraisal gap clause: what it is and how to cover the gap",
    excerpt:
      "What an appraisal gap clause is, how to cap it, gap clause vs waiving the contingency, and when it wins a bidding war — with a worked example for agents.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "how-to-negotiate-after-a-home-inspection",
    title: "How to negotiate after a home inspection",
    excerpt:
      "A home inspection turns up defects — now what? How agents turn the report into repairs, credits or a price cut without killing the deal, with a worked example.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "price-per-square-metre-valuation",
    title: "Price per square metre: how to value a home with it",
    excerpt:
      "How to use price per square metre to value a home: calculate it right, build a local benchmark from comps, avoid the traps, and use it with clients. With a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "property-valuation-report-template",
    title: "Property valuation report template for agents: structure & example",
    excerpt:
      "A property valuation report template for agents: the exact sections to include, a worked example, and how to brand it. Build a report clients trust.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "how-to-present-a-cma-to-a-seller",
    title: "How to present a CMA to a seller: a listing agent's guide",
    excerpt:
      "How to present a CMA to a seller: structure the meeting, lead with the price range, handle the seller who wants more, and close the listing. With a worked example.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "win-the-listing-with-pricing",
    title: "How to win the listing with a pricing strategy",
    excerpt:
      "Win the listing appointment with a pricing strategy: anchor on evidence, beat the agent who overprices, and handle the seller who wants more. With a worked example.",
    category: "Pricing",
    readingTime: 12,
  },
  {
    slug: "broker-price-opinion-vs-cma",
    title: "Broker price opinion (BPO) vs CMA: the difference",
    excerpt:
      "What a BPO is, how it differs from a CMA and from a full appraisal, who orders each, and when to use which — with a worked example for agents.",
    category: "Valuation",
    readingTime: 11,
  },
  {
    slug: "cma-report-what-to-include",
    title: "What to include in a CMA report: the essential sections",
    excerpt:
      "The sections every CMA report needs — subject property, comps, adjustments, competition, market conditions and a defensible price range. With a worked example.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "avm-vs-cma-which-to-use",
    title: "AVM vs CMA: which property valuation method to use",
    excerpt:
      "How an automated valuation model and a comparative market analysis differ, where each is accurate, and which to reach for — with a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "desktop-appraisal-vs-avm",
    title: "Desktop appraisal vs AVM vs full appraisal explained",
    excerpt:
      "How desktop appraisals, AVMs and full appraisals differ on cost, speed, accuracy and lender acceptance, and which to use — with a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "hedonic-pricing-model-explained",
    title: "What is a hedonic pricing model in real estate valuation",
    excerpt:
      "How a hedonic pricing model breaks a property's price into the implicit value of each feature, how it's built, and where it powers CMAs and AVMs. With a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "how-accurate-are-online-home-value-estimates",
    title: "How accurate are online home value estimates?",
    excerpt:
      "Where Zestimate-style AVMs are reliable, why they miss, and how agents should use and explain online home value estimates — with a worked example.",
    category: "Valuation",
    readingTime: 11,
  },
  {
    slug: "active-sold-expired-comps",
    title: "Active vs sold vs expired listings: which comps matter",
    excerpt:
      "Which listing status to price a CMA from and which only sets context: solds set the price, while pendings, actives and expireds set the position. With a worked example.",
    category: "CMA",
    readingTime: 11,
  },
  {
    slug: "best-real-estate-tools-for-agents",
    title: "Best real estate tools and apps for agents (2026)",
    excerpt:
      "The best real estate tools and apps for agents in 2026, organised by job: CRM, pricing/CMA, marketing, e-sign and more — plus how to cost your stack.",
    category: "Tools",
    readingTime: 12,
  },
  {
    slug: "real-estate-crm-for-agents",
    title: "Best real estate CRM for agents: what to look for (2026)",
    excerpt:
      "How to choose the best real estate CRM for agents: the features that matter, what to skip, and a worked cost-per-deal example to justify the spend.",
    category: "Tools",
    readingTime: 12,
  },
  {
    slug: "how-to-do-a-real-estate-market-analysis",
    title: "How to do a real estate market analysis: a step-by-step guide",
    excerpt:
      "Run a local real estate market analysis in 7 steps: inventory, absorption rate, days on market, sale-to-list and price trends, with a worked example.",
    category: "Market analysis",
    readingTime: 12,
  },
  {
    slug: "absorption-rate-real-estate",
    title: "Absorption rate in real estate: how to calculate and use it",
    excerpt:
      "Absorption rate explained for agents: the formula, seller's vs buyer's market thresholds, months of inventory, and a worked example you can copy.",
    category: "Market analysis",
    readingTime: 11,
  },
  {
    slug: "real-estate-lead-generation-ideas",
    title: "Real estate lead generation ideas for agents (2026)",
    excerpt:
      "Practical real estate lead generation ideas for agents: online, referral and farming tactics that actually produce listings, plus a worked funnel example.",
    category: "Lead generation",
    readingTime: 12,
  },
  {
    slug: "open-house-lead-generation-ideas",
    title: "Open house ideas to generate seller leads (agent guide)",
    excerpt:
      "Open house ideas that generate seller leads, not just buyer footfall: how agents plan, capture and follow up, with a worked cost-per-listing example.",
    category: "Lead generation",
    readingTime: 12,
  },
  {
    slug: "seller-lead-nurture-email-sequence",
    title: "Seller lead nurture email sequence for real estate agents",
    excerpt:
      "What to send and when to turn seller valuation leads into listings: a worked six-email nurture sequence for agents, with cadence and a payoff example.",
    category: "Lead generation",
    readingTime: 12,
  },
  {
    slug: "real-estate-landing-page-best-practices",
    title: "Real estate landing page best practices for lead conversion",
    excerpt:
      "Real estate landing page best practices that convert visitors into seller leads: structure, copy, the valuation hook, and a worked conversion example.",
    category: "Lead generation",
    readingTime: 12,
  },
  {
    slug: "listing-presentation-guide",
    title: "Listing presentation: how to make a winning one (agent guide)",
    excerpt:
      "What a listing presentation is and how to win it: structure, pricing with a CMA, the marketing plan and the close — a practical guide for real estate agents.",
    category: "Real estate agents",
    readingTime: 11,
  },
  {
    slug: "handle-seller-objections-listing",
    title: "How to handle seller objections in a listing appointment",
    excerpt:
      "A real estate agent's playbook for handling objections on price, commission, competing quotes and stalling — with a repeatable method and a worked example.",
    category: "Real estate agents",
    readingTime: 11,
  },
  {
    slug: "how-to-create-a-cma",
    title:
      "How to create a CMA (comparative market analysis): a step-by-step guide",
    excerpt:
      "Build an accurate comparative market analysis in 7 steps: pull the right comps, adjust for differences, set a defensible price range and present it. With a worked example.",
    category: "CMA",
    readingTime: 11,
  },
  {
    slug: "how-to-value-a-rental-property",
    title:
      "How to value a rental or investment property: the income approach explained",
    excerpt:
      "Value a rental the way investors do: cap rate, NOI, GRM and comps reconciled into one number. A step-by-step method for agents and investors, with a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "cma-price-adjustments-explained",
    title: "How to make price adjustments in a CMA (with examples)",
    excerpt:
      "How to make CMA price adjustments: choose the features, value each one with paired sales and apply them consistently to build a defensible price range.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "cma-software-for-real-estate-agents",
    title: "CMA software for real estate agents: how to choose (2026)",
    excerpt:
      "Compare CMA software for real estate agents: the data, features and pricing that actually matter, plus how to turn comps into a client-ready report fast.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "free-vs-paid-cma-tools",
    title: "Free vs paid CMA tools: what's the difference",
    excerpt:
      "Free vs paid CMA tools compared: what really differs in data, reports and speed, plus how agents decide when a paid tool is worth the cost.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "white-label-property-report-software",
    title: "White-label property report software for agents (2026)",
    excerpt:
      "How white-label property report software lets agents put their own brand on valuations and CMAs: what to brand, why it wins listings, and how to choose a tool.",
    category: "Software",
    readingTime: 12,
  },
  {
    slug: "cloud-cma-alternative",
    title: "Cloud CMA alternative: how to choose CMA software (2026)",
    excerpt:
      "Looking for a Cloud CMA alternative? The data, cost-per-report and migration checks that matter when switching CMA software, with a worked example.",
    category: "CMA",
    readingTime: 12,
  },
  {
    slug: "home-valuation-widget-for-your-website",
    title: "Home valuation widget for your website: a lead-gen guide",
    excerpt:
      "How a home valuation widget turns your real estate website into a seller-lead engine: what it does, what to look for, and how to set one up.",
    category: "Lead generation",
    readingTime: 12,
  },
  {
    slug: "idx-website-for-real-estate-agents",
    title: "What is an IDX website for real estate agents?",
    excerpt:
      "What an IDX website is, how it captures buyer leads on your own domain, what it costs, and how to set one up so it converts instead of just showing listings.",
    category: "Lead generation",
    readingTime: 13,
  },
  {
    slug: "what-is-gazumping-and-how-to-avoid-it",
    title: "Gazumping explained: what it is and how to avoid it",
    excerpt:
      "What gazumping is, why sellers do it, and the practical tactics buyers and agents use to avoid losing a home after an offer is accepted. With a worked example.",
    category: "Negotiation",
    readingTime: 11,
  },
  {
    slug: "how-to-find-comparable-sales-comps",
    title: "How to find comparable sales (comps) for a property",
    excerpt:
      "Find comparable sales the right way: where to source comps, the three filters that matter, how many you need and how to adjust them. With a worked example.",
    category: "CMA",
    readingTime: 11,
  },
  {
    slug: "what-is-an-avm-automated-valuation-model",
    title: "What is an automated valuation model (AVM) and is it accurate?",
    excerpt:
      "An automated valuation model (AVM) estimates a home's value from data in seconds. How AVMs work, how accurate they are and when to trust the number. With a worked example.",
    category: "Valuation",
    readingTime: 11,
  },
  {
    slug: "how-to-price-a-listing",
    title: "How to price a listing as a real estate agent: a complete guide",
    excerpt:
      "How to price a listing accurately: build the CMA, choose a pricing strategy, avoid overpricing and defend the number to the seller. With a worked example.",
    category: "Pricing",
    readingTime: 12,
  },
  {
    slug: "prelisting-package-guide",
    title:
      "The real estate prelisting package: what to send before the appointment",
    excerpt:
      "What to put in a prelisting package to win more listings: the six sections that matter, what to send digitally, and a worked example agenda.",
    category: "Listings",
    readingTime: 12,
  },
  {
    slug: "how-to-value-a-condo-apartment",
    title: "How to value a condo or apartment: comps, fees and a worked example",
    excerpt:
      "How to value a condo or apartment accurately: in-building comps, floor and view adjustments, service charges, price per square metre and a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "how-to-value-a-house",
    title: "How to value a house: methods, comps and a worked example",
    excerpt:
      "How to value a house accurately: the comparable sales method, price per square metre, automated valuations and a worked example you can follow step by step.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "how-much-should-i-offer-on-a-house",
    title: "How much should you offer on a house? A buyer's framework",
    excerpt:
      "How much should you offer on a house? A framework to value the home from comps, read the market and set your number and ceiling — with a worked example.",
    category: "Negotiation",
    readingTime: 12,
  },
  {
    slug: "borrowing-capacity-explained",
    title: "Borrowing capacity: how much can you actually borrow?",
    excerpt:
      "How lenders work out borrowing capacity from income, debts, rates and term — what moves the number, and how to size a client, with a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "how-interest-rates-affect-borrowing",
    title: "How interest rates affect how much you can borrow",
    excerpt:
      "How the interest rate resizes a client's mortgage budget through the monthly payment and stress test — with a worked example advisors can adapt.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "self-employed-mortgage-guide",
    title: "Self-employed mortgage: how lenders assess your income",
    excerpt:
      "Which income figure lenders actually use for sole traders, partners, directors and contractors, how years are averaged, and the evidence pack that gets a file approved.",
    category: "Mortgages",
    readingTime: 13,
  },
  {
    slug: "mortgage-stress-test-explained",
    title: "Mortgage stress test: why lenders test a higher rate",
    excerpt:
      "How lenders build the assessment rate from reversion rates, buffers and floors — what it costs a client in borrowing power, and how to pass it.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "fixed-vs-variable-mortgage-rate",
    title: "Fixed vs variable mortgage rate: how to advise on it",
    excerpt:
      "What the variable discount really pays for, a worked break-even example, and the client circumstances that genuinely change the answer.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "debt-to-income-ratio-explained",
    title: "Debt-to-income ratio (DTI) explained for mortgages",
    excerpt:
      "How lenders calculate DTI, front-end vs back-end, what counts as a good ratio, and how to lower it before applying — with a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "how-to-calculate-home-equity",
    title: "How to calculate home equity (and why it matters)",
    excerpt:
      "The simple formula, a worked example, and how equity drives borrowing, refinancing and selling — with the valuation discipline that makes the number stick.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "mortgage-advisor-client-report-tools",
    title: "Client affordability report tools for mortgage advisors",
    excerpt:
      "How to choose affordability report tools as a mortgage advisor — the three-layer toolstack, what a client-ready report must include, and the property-side check. With a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "how-much-deposit-do-you-need",
    title: "How much deposit do you need to buy a house?",
    excerpt:
      "The 5%, 10% and 20% deposit tiers, the purchase costs nobody budgets for, and the valuation gap that sinks deals — with a worked example for advisors.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "interest-only-vs-repayment-mortgage",
    title: "Interest-only vs repayment mortgage: which is right?",
    excerpt:
      "The real cost gap between interest-only and repayment, the repayment-vehicle test lenders apply, and the part-and-part middle ground advisors underuse.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "refinancing-a-mortgage-explained",
    title: "Refinancing a mortgage: how it works and when it pays off",
    excerpt:
      "The break-even calculation advisors should run in the first meeting, why the valuation decides the rate tier, and the four cases where doing nothing is the right advice.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "mortgage-affordability-explained",
    title: "Mortgage affordability: how lenders decide what you can afford",
    excerpt:
      "How lenders assess affordability — residual income, commitments, DTI caps and the stress test — and how advisors pre-empt a decline, with a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "guarantor-mortgage-explained",
    title: "Guarantor mortgage explained: how it works and who it suits",
    excerpt:
      "How a guarantor mortgage works, who it suits and what the guarantor is really liable for — with a worked example for advisors and buyers' agents.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "negative-equity-explained",
    title: "Negative equity explained: what it means and what to do",
    excerpt:
      "What negative equity means, how a home ends up underwater, and the real options — overpay, wait, improve, sell or port — with a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "loan-to-value-ltv-explained",
    title: "Loan-to-value (LTV) ratio explained",
    excerpt:
      "How to calculate LTV, why it sets the rate and deposit, and how a low valuation pushes the ratio up and can break a deal — with a worked example.",
    category: "Mortgages",
    readingTime: 12,
  },
  {
    slug: "how-to-win-a-bidding-war",
    title: "How to win a bidding war on a house: an agent's playbook",
    excerpt:
      "How to win a bidding war on a house: value the home, set a ceiling, sharpen your terms and beat higher offers on certainty — with a worked example.",
    category: "Negotiation",
    readingTime: 13,
  },
  {
    slug: "how-to-tell-if-a-house-is-overpriced",
    title: "How to tell if a house is overpriced: 7 signs",
    excerpt:
      "How to tell if a house is overpriced: 7 evidence-based signs, from comps and price per square metre to days on market — with a worked example.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "how-to-spot-an-underpriced-house",
    title: "How to spot an underpriced house (and avoid the traps)",
    excerpt:
      "How to spot an underpriced house: the signals that reveal genuine value, the traps that mimic a bargain, and a worked example you can reuse.",
    category: "Valuation",
    readingTime: 12,
  },
  {
    slug: "documents-needed-to-buy-a-house",
    title: "What documents do you need to buy a house? A checklist",
    excerpt:
      "The documents you need to buy a house: ID, proof of income, proof of funds, the offer and the mortgage paperwork — plus how agents keep the file moving.",
    category: "Documents",
    readingTime: 11,
  },
  {
    slug: "proof-of-funds-explained",
    title: "Proof of funds letter for buying a house: a full guide",
    excerpt:
      "What a proof of funds letter is, what it must show, and how agents use it to verify buyers and keep offers moving — with a worked example.",
    category: "Documents",
    readingTime: 11,
  },
  {
    slug: "how-to-read-a-purchase-contract",
    title: "How to read a real estate purchase contract before signing",
    excerpt:
      "The clauses, conditions, deposit and dates that carry the risk in a purchase contract — and how agents stop surprises before a client signs.",
    category: "Documents",
    readingTime: 11,
  },
  {
    slug: "seller-disclosure-statement-explained",
    title: "Seller disclosure statement explained: what to declare",
    excerpt:
      "What a seller disclosure statement is, what must be declared, how it differs from an inspection, and how agents handle it — with a worked example.",
    category: "Documents",
    readingTime: 12,
  },
  {
    slug: "home-appraisal-process-explained",
    title: "The home appraisal process: what to expect",
    excerpt:
      "What happens in a home appraisal, step by step: how it's ordered, what the appraiser checks on site, how the value is built from comps, and what to do with the number. With a worked example.",
    category: "Valuation",
    readingTime: 13,
  },
  {
    slug: "onder-de-vraagprijs-bieden",
    title: "Kun je onder de vraagprijs bieden? Zo pak je het aan (2026)",
    excerpt:
      "Ja, dat mag altijd. En het is kansrijker dan je denkt: in 2025 werd 32% op of onder de vraagprijs verkocht. De 5 signalen, voorbeeldzinnen en een rekenvoorbeeld.",
    category: "Bieden",
    readingTime: 13,
  },
  {
    slug: "voorbehoud-financiering",
    title: "Financieringsvoorbehoud: wat het is en wat erin moet (2026)",
    excerpt:
      "Wat er in het financieringsvoorbehoud moet staan (termijn, bedrag, documentatieplicht), het 10% boeterisico als je zonder biedt en 4 veiligere alternatieven.",
    category: "Financiering",
    readingTime: 13,
  },
  {
    slug: "bedenktijd-huis-kopen",
    title: "Bedenktijd bij het kopen van een huis: zo werken de 3 dagen",
    excerpt:
      "Drie dagen wettelijke bedenktijd, waarvan minimaal twee werkdagen. Hoe je ze telt (met tabel), wanneer ze ingaan en hoe je kosteloos ontbindt.",
    category: "Koopproces",
    readingTime: 13,
  },
  {
    slug: "biedstrategie-huis-kopen",
    title: "Biedstrategie: zo bepaal je een slim bod op een huis (2026)",
    excerpt:
      "Bied slim, niet hoog. Het complete stappenplan: marktwaarde bepalen, je maximum berekenen, de vraagprijs lezen en voorwaarden inzetten als hefboom.",
    category: "Strategie",
    readingTime: 14,
  },
  {
    slug: "hoeveel-overbieden-nederland-2026",
    title: "Hoeveel overbieden in Nederland in 2026?",
    excerpt:
      "In Q4 2025 werd in Nederland gemiddeld 6,8% boven de vraagprijs geboden. Maar dat landelijke gemiddelde vertelt niet het hele verhaal.",
    category: "Overbieden",
    readingTime: 15,
  },
  {
    slug: "verschil-taxatiewaarde-en-vraagprijs",
    title: "Verschil taxatiewaarde en vraagprijs: wat moet je weten?",
    excerpt:
      "In 2025 liep bij 14% van alle hypotheekaanvragen de taxatie lager uit dan de vraagprijs. Wat is het verschil, en wat kun je doen?",
    category: "Taxatie",
    readingTime: 13,
  },
  {
    slug: "taxatie-lager-dan-koopsom-wat-nu",
    title: "Taxatie lager dan de koopsom? Zo los je het tekort op (2026)",
    excerpt:
      "De taxatie komt maar in ~8% van de aankopen onder de koopsom — maar dan betaal je het verschil zelf. Zo bereken je het tekort en zo los je het op.",
    category: "Taxatie",
    readingTime: 12,
  },
  {
    slug: "hoeveel-eigen-geld-bij-overbieden",
    title: "Hoeveel eigen geld heb je nodig bij overbieden?",
    excerpt:
      "In 2026 heeft de gemiddelde koper €48.000 eigen geld nodig bij een bod van €525k. Hoe bereken je dat, en hoe kun je het verlagen?",
    category: "Financiering",
    readingTime: 12,
  },
  {
    slug: "woz-vs-vraagprijs-bod-te-hoog",
    title: "WOZ vs vraagprijs: is je bod te hoog?",
    excerpt:
      "Als de vraagprijs meer dan 15% boven de WOZ ligt, is het risico groot dat je taxatie lager uitvalt. Maar die 15% is geen harde grens.",
    category: "WOZ",
    readingTime: 13,
  },
  {
    slug: "overbieden-meefinancieren-hypotheek",
    title: "Kun je overbieden meefinancieren in de hypotheek?",
    excerpt:
      "Nee, je kunt overbieden niet direct meefinancieren — maar er zijn 4 scenario's waarin je indirect toch meer kunt lenen.",
    category: "Hypotheek",
    readingTime: 12,
  },
  {
    slug: "bod-uitbrengen-wat-staat-erin",
    title: "Bod uitbrengen: wat staat erin en hoe werkt het?",
    excerpt:
      "Een bod is geen e-mailtje met een bedrag — het is een bindend aanbod met 6 standaardclausules. Wat moet erin en hoe bescherm je jezelf?",
    category: "Koopproces",
    readingTime: 14,
  },
  {
    slug: "overbieden-amsterdam-q1-2026",
    title: "Overbieden Amsterdam Q1 2026: cijfers per stadsdeel",
    excerpt:
      "In Q1 2026 werd in Amsterdam gemiddeld 9,2% overboden (€51k op €555k). Maar Zuid 11,4%, Noord 7,1%. Cijfers per stadsdeel + trends.",
    category: "Amsterdam",
    readingTime: 11,
  },
  {
    slug: "wel-of-niet-overbieden-beslisboom",
    title: "Wel of niet overbieden? De beslisboom",
    excerpt:
      "Moet je overbieden? Doorloop deze 5-staps beslisboom: tijd op markt, interesse, WOZ-ratio, buffer, motivatie. Incl. 3 voorbeelden.",
    category: "Strategie",
    readingTime: 13,
  },
  {
    slug: "beste-buurten-amsterdam-2026",
    title: "De 7 beste buurten in Amsterdam om een huis te kopen (2026)",
    excerpt:
      "Welke buurten scoren het best op veiligheid, voorzieningen en prijs? Onze data-gedreven top 7, met prijs per m², overbod-% en taxatierisico per buurt.",
    category: "Buurten",
    readingTime: 12,
  },
  {
    slug: "best-neighbourhoods-amsterdam-2026",
    title: "The 7 best neighbourhoods in Amsterdam to buy a house (2026)",
    excerpt:
      "Which Amsterdam neighbourhoods score best on safety, amenities and price? Our data-driven top 7, with price per m², overbidding % and appraisal risk per area.",
    category: "Neighbourhoods",
    readingTime: 12,
  },
  {
    slug: "beste-buurten-utrecht-2026",
    title: "De 7 beste buurten in Utrecht om een huis te kopen (2026)",
    excerpt:
      "Welke buurten scoren het best op veiligheid, voorzieningen en prijs? Onze data-gedreven top 7, met prijs per m², overbod-% en taxatierisico per buurt.",
    category: "Buurten",
    readingTime: 12,
  },
  {
    slug: "best-neighbourhoods-utrecht-2026",
    title: "The 7 best neighbourhoods in Utrecht to buy a house (2026)",
    excerpt:
      "Which Utrecht neighbourhoods score best on safety, amenities and price? Our data-driven top 7, with price per m², overbidding % and appraisal risk per area.",
    category: "Neighbourhoods",
    readingTime: 12,
  },
  {
    slug: "huis-kopen-stappenplan-2026",
    title: "Huis kopen: het complete stappenplan (2026)",
    excerpt:
      "Een huis kopen in Nederland in 2026: het complete stappenplan in 8 stappen — van maximale hypotheek en bod tot bedenktijd, ontbindende voorwaarden en de notaris.",
    category: "Koopproces",
    readingTime: 13,
  },
  {
    slug: "buying-a-house-netherlands-2026",
    title:
      "Buying a house in the Netherlands: the complete step-by-step guide (2026)",
    excerpt:
      "Buying a house in the Netherlands in 2026: the complete process in 8 steps — from maximum mortgage and offer to the cooling-off period, conditions and the notary.",
    category: "Process",
    readingTime: 13,
  },
  {
    slug: "maximale-hypotheek-2026",
    title: "Maximale hypotheek 2026: hoeveel kun je lenen?",
    excerpt:
      "Hoeveel kun je lenen op basis van je inkomen, de rente en het energielabel? Met rekenvoorbeeld, indicatietabel per inkomen en de wijzigingen voor 2026.",
    category: "Hypotheek",
    readingTime: 11,
  },
  {
    slug: "maximum-mortgage-netherlands-2026",
    title: "Maximum mortgage in the Netherlands 2026: how much can you borrow?",
    excerpt:
      "How much can you borrow based on income, rate and energy label? With a worked example, an income table and the 2026 changes for expats.",
    category: "Mortgage",
    readingTime: 11,
  },
  {
    slug: "energielabel-woning-2026",
    title: "Energielabel woning 2026: wat betekenen A tot G?",
    excerpt:
      "Wat betekenen de labels A++++ tot G, wat kost een label, is het verplicht bij verkoop, en hoeveel extra hypotheek levert een goed label op?",
    category: "Energielabel",
    readingTime: 10,
  },
  {
    slug: "energy-label-house-netherlands-2026",
    title:
      "Energy label for a house in the Netherlands 2026: what do A to G mean?",
    excerpt:
      "What the labels A++++ to G mean, what a label costs, whether it's mandatory when selling, and how much extra mortgage a good label allows.",
    category: "Energy label",
    readingTime: 10,
  },
  {
    slug: "kosten-koper-2026",
    title: "Kosten koper 2026: wat betaal je bovenop de koopsom?",
    excerpt:
      "Wat valt onder kosten koper en hoeveel is het? Overdrachtsbelasting, notaris, taxatie, advies en NHG — met rekenvoorbeeld en het totaal (~6%).",
    category: "Kosten koper",
    readingTime: 10,
  },
  {
    slug: "buyers-costs-netherlands-2026",
    title:
      "Buyer's costs in the Netherlands 2026: what do you pay on top of the price?",
    excerpt:
      "What's included in buyer's costs and how much? Transfer tax, notary, appraisal, advice and NHG — with a worked example and the ~6% total.",
    category: "Buyer's costs",
    readingTime: 10,
  },
];
